import { createRoute } from "@tanstack/react-router";
import { Route as TenantLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useToast } from "@/components/application/notifications/toast-provider";
import {
  useAssignCardHolder,
  useTenantCards,
  useTenantHolders,
  type TenantCard,
} from "../../hooks/useTenantCards";

/**
 * Všechny karty střediska (tenantAdmin) — přehled karet tenanta + držitel u každé,
 * s možností přiřadit/změnit držitele (self-service). Číslo karty maskované.
 */

function TenantCardsPage() {
  const { data, isLoading, error } = useTenantCards();
  const holders = useTenantHolders();
  const assign = useAssignCardHolder();
  const toast = useToast();

  const cards = data?.cards ?? [];
  const holderOptions = (holders.data?.rows ?? []).filter((h) => !h.deactivated_at);

  async function onAssign(fuelCardId: string, holderId: string | null) {
    try {
      await assign.mutateAsync({ fuelCardId, holderId });
      toast.push({ variant: "success", message: holderId ? "Držitel přiřazen." : "Držitel odebrán." });
    } catch (e) {
      toast.push({ variant: "error", message: e instanceof Error ? e.message : "Přiřazení selhalo." });
    }
  }

  const columns: Column<TenantCard>[] = [
      {
        key: "card",
        header: "Karta",
        cell: (r) => (
          <div className="flex flex-col gap-0.5">
            <strong className="text-primary">{r.card_label ?? "Tankovací karta"}</strong>
            {r.card_number_display && (
              <span className="text-xs text-tertiary">{r.card_number_display}</span>
            )}
          </div>
        ),
      },
      {
        key: "status",
        header: "Stav",
        cell: (r) => (
          <BadgeWithDot color={r.active ? "success" : "gray"}>
            {r.active ? "Aktivní" : r.status}
          </BadgeWithDot>
        ),
      },
      { key: "network", header: "Síť", cell: (r) => r.network ?? "—" },
      { key: "expiry", header: "Platnost do", cell: (r) => r.expiry_date ?? "—" },
      {
        key: "holder",
        header: "Držitel",
        cell: (r) => (
          <div className="flex flex-col gap-1">
            <select
              className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
              value={r.holder_id ?? ""}
              disabled={assign.isPending}
              onChange={(e) => void onAssign(r.fuel_card_id, e.target.value === "" ? null : e.target.value)}
              aria-label={`Držitel karty ${r.card_label ?? r.fuel_card_id}`}
              data-testid={`tenant-card-holder-${r.fuel_card_id}`}
            >
              <option value="">— nepřiřazeno —</option>
              {holderOptions.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.display_name}
                  {h.holder_type === "legal" ? " (PO)" : ""}
                </option>
              ))}
            </select>
          </div>
        ),
      },
    ];

  return (
    <section aria-labelledby="tenant-cards-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="tenant-cards-title" className="text-2xl font-bold text-primary m-0">
          Všechny karty střediska
        </h2>
        <p className="text-base text-tertiary m-0">
          Přehled tankovacích karet střediska a jejich držitelů. Držitele lze přiřadit přímo zde.
        </p>
      </header>

      {error && (
        <InlineAlert variant="error">
          {error instanceof Error ? error.message : "Načtení karet selhalo."}
        </InlineAlert>
      )}

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">
            Karty
            {!isLoading && <span className="ml-2 text-sm font-normal text-tertiary">({cards.length})</span>}
          </h3>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <LoadingIndicator label="Načítání…" />
          ) : (
            <DataTable
              caption="Karty střediska s držiteli"
              columns={columns}
              rows={cards}
              rowKey={(r) => r.fuel_card_id}
              emptyMessage="Žádné karty střediska."
            />
          )}
        </CardBody>
      </Card>

      <p className="text-xs text-tertiary">
        <Badge color="gray">Pozn.</Badge> Číslo karty je z bezpečnostních důvodů zkrácené.
      </p>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/cards",
  component: TenantCardsPage,
});
